/**
 * Pool settings drawer — single drawer that owns every per-pool runtime
 * governance knob: roster (members), trigger mode, agent supervisors, and
 * (Phase D) connector overrides.
 *
 * Renamed from ``PoolMembersDrawer`` once the surface outgrew "members" —
 * see ``.cursor/rules/connector-context-hierarchy.mdc`` for the full
 * Tenant ⊃ Network ⊃ Pool model. The trigger label in
 * ``AgenticNetworkRoom`` is "Settings" with a member-count badge so
 * supervisors don't lose the at-a-glance roster size.
 *
 * Authz surface (enforced server-side in ``rest_handlers.py``):
 *   - Pool CREATOR can mutate every section (members, trigger, supervisors,
 *     connector overrides). Mirrors the supervisor-override pattern.
 *   - Workspace OWNER/ADMIN can also manage via Overwatch escalation.
 *   - Pool SUPERVISOR can only view.
 *
 * Live refresh: every section subscribes to its own AppSync event — roster
 * to ``MEMBERSHIP_*``, supervisors to ``POOL_AGENT_SUPERVISOR_CHANGED``,
 * connectors to ``POOL_CONNECTOR_OVERRIDE_CHANGED``, trigger mode to
 * ``POOL_SETTINGS_CHANGED``. Each event has its own de-dupe ref so a
 * burst on one section doesn't mask events on another.
 *
 * UI: governance blocks (Members · invite & roster; Trigger mode; Agent
 * supervisors; Connectors) are collapsible Sidebar-style disclosures
 * (\`\`rotate-90\`\` chevron when open); all start **collapsed by default**.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  addPoolMember,
  clearAgentMcpInstanceOverride,
  clearAgentPoolConnectorOverride,
  clearPoolAgentSupervisor,
  clearPoolConnectorOverride,
  listAgentConnectorOverrides,
  listAgentMcpOverrides,
  listPoolAgentSupervisors,
  listPoolConnectorOverrides,
  listPoolMembers,
  removePoolMember,
  setAgentMcpInstanceOverride,
  setAgentPoolConnectorOverride,
  setPoolAgentSupervisor,
  setPoolConnectorOverride,
  setPoolMcpInstanceOverride,
  clearPoolMcpInstanceOverride,
  getRoomContextOverride,
  setRoomContextOverride,
  clearRoomContextOverride,
  setPoolJudgeModel,
  updatePoolSettings,
} from '@/services/poolMembersApi';

// Sentinel agent-id for the "Room Wide" scope in the Tools picker. Selecting it
// pins connectors/MCPs pool-wide (every agent in the room) instead of for one
// agent — useful for common tools shared across rooms in one scope. A real
// per-agent pin still beats a Room-Wide pin at resolution time.
const ROOM_WIDE_AGENT_ID = '__room__';

// Reserved network pseudo-agent ids that must NEVER appear as selectable agents
// in the room. ``__roomContext__`` is the network SHARED tier — its tools already
// flow into the "Room Wide (all agents)" bucket, so surfacing it as a separate
// agent would be a confusing duplicate. Defensive mirror of the backend
// ``is_real_agent_tail`` filter (a stale backend must not reintroduce the row).
const RESERVED_AGENT_IDS = new Set(['__roomContext__', 'Shared']);
const realAgentsOnly = (list) =>
  (Array.isArray(list) ? list : []).filter((a) => !RESERVED_AGENT_IDS.has(a?.agentId));

/**
 * Group MCP credential rows by mcpId for the room's tool picker, canonicalizing
 * on (mcpId, instanceId) so each pair yields exactly ONE entry.
 *
 * WHY: a room sees BOTH its own POOL-scoped shell AND the inherited NETWORK
 * credential at the SAME ``instanceId 'default'``. A pin is only ``{id, instanceId}``
 * — it carries NO scope — so those two rows are indistinguishable to a pin, and
 * the picker must offer the ONE scope-resolution would actually use. Rank
 * most-specific first (POOL ▸ NETWORK ▸ TENANT) so the room's own shell shadows
 * the inherited network default (mirrors the runtime union dedup + mcpPinLabel).
 * Distinct labeled instances (``mi_*``) keep separate instanceIds, so they're
 * never collapsed — only the shared-'default' collision is resolved.
 *
 * ``poolScoreable`` gates POOL's rank: only a room mount (poolId present) lets a
 * POOL row win; at a network mount POOL rows shouldn't outrank NETWORK.
 *
 * @param {Array<{mcpId:string, instanceId?:string, label?:string, displayName?:string, scope?:{kind?:string}}>} rows
 * @param {{ poolScoreable?: boolean }} [opts]
 * @returns {{ [mcpId:string]: Array<{instanceId:string, label:string, scope:any}> }}
 */
export function groupMcpInstancesByScope(rows, { poolScoreable = true } = {}) {
  const rank = { POOL: poolScoreable ? 3 : 0, NETWORK: 2, TENANT: 1 };
  const byId = {};
  const bestRankFor = {};  // `${mcpId}::${instanceId}` → chosen scope rank
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r || !r.mcpId) continue;
    const iid = r.instanceId || 'default';
    const key = `${r.mcpId}::${iid}`;
    const rk = rank[String(r.scope?.kind || '').toUpperCase()] || 0;
    const list = (byId[r.mcpId] = byId[r.mcpId] || []);
    const entry = {
      instanceId: iid,
      label: r.label || r.displayName || r.mcpId,
      scope: r.scope,
    };
    if (!(key in bestRankFor)) {
      bestRankFor[key] = rk;
      list.push(entry);
    } else if (rk > bestRankFor[key]) {
      // A more-specific scope for the same (mcpId, instanceId) wins — replace in
      // place so consumers see the room's own row, not the inherited network one.
      bestRankFor[key] = rk;
      const idx = list.findIndex((e) => (e.instanceId || 'default') === iid);
      if (idx >= 0) list[idx] = entry; else list.push(entry);
    }
    // rk <= existing → keep the more-specific one already stored (drop this).
  }
  return byId;
}
import CollapsibleSection from '@stepflow/ui-shared/components/CollapsibleSection.jsx';
import WorkspaceMembersMultiPicker from '@stepflow/ui-shared/components/workspace/WorkspaceMembersMultiPicker.jsx';
import AgentToolPicker from '@stepflow/ui-shared/components/agentTools/AgentToolPicker.jsx';
import {
  listMcpRegistry, listMcpCredentials, listSecretsRegistry, listTools,
  listAgentWorkspaceOverrides, setAgentWorkspaceOverride, clearAgentWorkspaceOverride,
  setPoolWorkspaceOverride, clearPoolWorkspaceOverride,
} from '@stepflow/ui-shared/services/toolsApi';
import {
  TRIGGER_HYBRID,
  listTriggerModes,
  normalizeTriggerMode,
} from '@stepflow/ui-shared/utils/networkTrigger';

/** Human copy for registry tools operators might pin — does not constrain which rows appear (discovered from secrets list + overrides API). */
function presentationForConnectorTool(toolName) {
  const tn = toolName.trim();
  if (tn === 'code-connector') {
    return {
      label: 'Git connection (code-connector)',
      description:
        'Universal Builder clones / pulls / pushes through this connector when the network runs UB. ' +
        'Pin another registry row for this pool without re-releasing.',
    };
  }
  if (tn === 'generic-rest-api') {
    return {
      label: 'REST API credential (generic-rest-api)',
      description:
        'Saved HTTP integrations for REST-capable tooling. Override here mirrors the Tenant ⊃ Network ⊃ Pool registry chain.',
    };
  }
  return {
    label: tn.replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase()),
    description:
      'Registry connection rows for this tool visible to this pool. Pin overrides on the keys the runtime merges at execute time.',
  };
}

/** Provenance badge for effective connector resolution (matrix API ``source``). */
function connectorSourcePresentation(source) {
  const s = (source || '').toUpperCase();
  if (s === 'OVERRIDE') {
    return {
      short: 'Agent pin',
      cls: 'bg-amber-50 text-amber-800',
      title: 'This agent has a per-agent pool override',
    };
  }
  if (s === 'LEGACY_POOL') {
    return {
      short: 'Pool default',
      cls: 'bg-sky-50 text-sky-900',
      title: 'Inherits the pool-wide connector override',
    };
  }
  if (s === 'NETWORK_DEFAULT') {
    return {
      short: 'Network default',
      cls: 'bg-gray-100 text-gray-700',
      title: 'Uses the default from the released network snapshot',
    };
  }
  if (s === 'AGENT') {
    return {
      short: 'From network',
      cls: 'bg-emerald-50 text-emerald-800',
      title: 'Connector assigned to this agent at build time (frozen in the release). Override it here to change just this room.',
    };
  }
  return {
    short: 'None',
    cls: 'bg-gray-50 text-gray-500',
    title: 'No connector secret in scope',
  };
}

// Match the Workbench's TOOLS_ENV derivation so the AgentToolPicker
// resolves SSM secret paths the same way in the drawer as it does in
// the Network Workbench. Defaults to ``"dev"`` like there.
const TOOLS_ENV = (import.meta.env.VITE_TOOLS_ENV || import.meta.env.VITE_ENV || 'dev')
  .toString()
  .trim()
  .toLowerCase();

// Legacy single-select kept as a fallback. The multi-role UI below uses
// ASSIGNABLE_ROOM_ROLES from roomRoles.js (OWNER, BUILDER, SUPERVISOR, VIEWER).
const ROLE_OPTIONS = [
  { value: 'SUPERVISOR', label: 'Supervisor (approve / view)' },
  { value: 'CREATOR', label: 'Creator (co-owner)' },
];

// Multi-role assignment uses the new room-role taxonomy. Backend accepts
// either ``role`` (legacy single) or ``roles`` (new array); we send ``roles``.
const ROOM_ROLE_OPTIONS = [
  { value: 'OWNER', label: 'Owner', hint: 'Full control. Can respond to builder approvals and manage roster.' },
  { value: 'BUILDER', label: 'Builder', hint: 'Can respond to builder approvals.' },
  { value: 'SUPERVISOR', label: 'Supervisor', hint: 'Can view stream and audit. Read-only.' },
  { value: 'VIEWER', label: 'Viewer', hint: 'Spectator. Read-only.' },
];

/** Sidebar-style disclosure (matches ``Network Workbench`` in ``Sidebar.jsx``). */
// Thin wrapper over the shared ``CollapsibleSection`` so both the room drawer
// and the "Create a Room" release dialog render identical section headers.
function PoolSettingsCollapsible(props) {
  return <CollapsibleSection {...props} />;
}

function RoleBadge({ role }) {
  const isCreator = (role || '').toUpperCase() === 'CREATOR';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
        isCreator
          ? 'bg-indigo-50 text-indigo-700'
          : 'bg-gray-100 text-gray-700'
      }`}
    >
      {isCreator ? 'Creator' : 'Supervisor'}
    </span>
  );
}

export default function PoolSettingsDrawer({
  open,
  onClose,
  networkId,
  poolId,
  poolLabel,
  /** current user's Cognito sub — used to protect self-delete and show "(you)" */
  currentUserSub,
  /** true if the viewer can mutate the roster (pool OWNER or workspace OWNER/ADMIN) */
  canManage = false,
  /** true if the viewer can pin / clear runtime connector credentials. Wider
   *  than ``canManage`` — accepts pool BUILDER too because they're the role
   *  that responds to Universal Builder approvals at runtime. */
  canManageConnectors = false,
  /** optional channel-events feed (from useAgentChannel) to drive live refresh */
  channelEvents,
  /** called whenever the active-member count changes so the parent can
   *  show a "Members · N" badge in the room header without duplicating
   *  the REST fetch. Count excludes REVOKED rows. */
  onRosterChange,
  /** Phase B: current trigger mode (HEADLESS / HYBRID / MANUAL). Owned
   *  by the parent room so the prompt-box gating and the drawer card
   *  always agree on the latest value. */
  triggerMode,
  /** Notify parent when the trigger mode changes — same idea as
   *  ``onRosterChange`` but for the runtime-governance card. */
  onTriggerModeChange,
  /** Organization workspace tenant id — required for workspace member directory pickers. */
  tenantId = '',
  /** Forward-only "Create in Connections": when the tool picker's instance list
   *  is empty, switch the room to its Connections tab + auto-open the wizard.
   *  ``({ tool }) => void``. The drawer unmounts on close, so there's no
   *  auto-return — the user re-opens the drawer and picks the new connection. */
  onNavigateToConnections = null,
}) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('SUPERVISOR');
  // Multi-role selection for the new room-role taxonomy. Default: SUPERVISOR
  // for new invites (least privileged useful default). Owner can toggle on
  // OWNER + BUILDER + others as needed.
  const [selectedRoles, setSelectedRoles] = useState(['SUPERVISOR']);
  const toggleRole = (value) => {
    setSelectedRoles((prev) => {
      const has = prev.includes(value);
      if (has) {
        const next = prev.filter((r) => r !== value);
        return next.length === 0 ? ['SUPERVISOR'] : next; // never allow empty
      }
      return [...prev, value];
    });
  };
  const [triggerBusy, setTriggerBusy] = useState(false);
  const [triggerMsg, setTriggerMsg] = useState(null);
  // Phase C — per-agent supervisor overrides. Loaded only when the
  // drawer opens; the runtime resolver hits Dynamo directly so we
  // don't need a live listing to be correct, only to be useful.
  const [agentSupervisors, setAgentSupervisors] = useState([]);
  const [agentSupLoading, setAgentSupLoading] = useState(false);
  const [agentSupError, setAgentSupError] = useState(null);
  // Per-agent dropdown state for the supervisor section — same UX shape
  // as the connector section above. ``selectedSupervisorAgentId`` drives
  // which row's editor is shown; ``supervisorDraftEmails`` is the
  // unsaved picker value (committed via Save).
  const [selectedSupervisorAgentId, setSelectedSupervisorAgentId] = useState('');
  const [supervisorDraftEmails, setSupervisorDraftEmails] = useState([]);
  // Track which agent has the inline override editor open + per-row
  // busy / message state. Keyed by agentId so two operators (or two
  // tabs) can have independent rows in flight.
  const [editingAgentId, setEditingAgentId] = useState(null);
  const [overrideDraftEmails, setOverrideDraftEmails] = useState([]);
  const [overrideBusyId, setOverrideBusyId] = useState(null);
  const [overrideMsg, setOverrideMsg] = useState(null);
  // Phase D — connector overrides. Per-agent matrix (released snapshot × tools)
  // plus pool-wide legacy rows. ``connectorBusyKey`` is ``agent:{aid}:{tool}`` or
  // ``pool:{tool}``.
  const [agentConnectorAgents, setAgentConnectorAgents] = useState([]);
  const [poolWideConnectorOverrides, setPoolWideConnectorOverrides] = useState({});
  const [connectorChoices, setConnectorChoices] = useState({});
  const [connectorLoading, setConnectorLoading] = useState(false);
  const [connectorError, setConnectorError] = useState(null);
  const [connectorBusyKey, setConnectorBusyKey] = useState(null);
  const [connectorMsg, setConnectorMsg] = useState(null);
  // Per-agent picker state — Workbench-style "Tools & Routing" UX in the
  // drawer. The user picks one agent at a time; the picker emits an
  // array of tool entries; Save diffs that against the baseline (per-
  // agent overrides currently in DDB) and fires set / clear calls.
  const [selectedConnectorAgentId, setSelectedConnectorAgentId] = useState('');
  const [connectorAgentDraft, setConnectorAgentDraft] = useState([]);
  const [connectorAgentBaseline, setConnectorAgentBaseline] = useState([]);
  // Per-agent MCP-instance pins, folded into the SAME Tools picker as
  // connectors. ``mcpOverrides`` is the room-wide list ([{agentId,mcpId,instanceId}]);
  // ``mcpPinDraft``/``mcpPinBaseline`` are the selected agent's pins shaped as
  // AgentToolPicker MCP entries {id, instanceId, label}.
  const [mcpCatalog, setMcpCatalog] = useState([]); // authenticated MCP registry rows
  const [mcpOverrides, setMcpOverrides] = useState([]);
  // Per-agent MCPs FROZEN into the room's release ([{agentId,mcpId,instanceId,label}]).
  // These are INHERITED — shown as chips even with no room pin, mirroring how
  // connectors surface their frozen tool stamps. A pin overrides the instance.
  const [mcpFrozen, setMcpFrozen] = useState([]);
  const [mcpPinDraft, setMcpPinDraft] = useState([]);
  const [mcpPinBaseline, setMcpPinBaseline] = useState([]);
  // Configured MCP credential rows visible to this room (all scopes), used to
  // label a pin's chip with the ROOM's own instance name (e.g. "kyn GitHub")
  // rather than the generic registry name. A pin carries only {mcpId, instanceId}
  // — the label lives on the credential row, and instanceId 'default' collides
  // across scopes, so we resolve preferring the most-specific scope (POOL).
  const [mcpCredRows, setMcpCredRows] = useState([]);
  // Workspace pins (doc/arch/ASSETS.md): shared workspaces visible to this pool
  // (from the same registry chain, toolName='workspace') + the ROOM-WIDE pin.
  // One shared workspace per room (every agent collaborates on the one tree),
  // so the draft is a single workspaceId (or '').
  const [workspaceChoices, setWorkspaceChoices] = useState([]); // [{secretId, secretName}]
  const [workspaceOverrides, setWorkspaceOverrides] = useState([]); // [{agentId?, workspaceId}]
  const [workspacePinDraft, setWorkspacePinDraft] = useState('');
  const [workspacePinBaseline, setWorkspacePinBaseline] = useState('');
  const [connectorAgentBusy, setConnectorAgentBusy] = useState(false);
  const [connectorToolsCatalog, setConnectorToolsCatalog] = useState({
    tools: [],
    loading: false,
    error: '',
  });
  const [builderMode, setBuilderMode] = useState(null);
  const [builderModeBusy, setBuilderModeBusy] = useState(false);
  const [builderModeMsg, setBuilderModeMsg] = useState(null);
  const [settingsSectionOpen, setSettingsSectionOpen] = useState({
    members: false,
    trigger: false,
    builder: false,
    supervisors: false,
    connectors: false,
    connections: false,
    roomContext: false,
  });
  // Authenticated-MCP catalog — folded into the connector "Tools" picker so a
  // room operator pins WHICH configured instance an agent uses (an instanceId
  // pointer, not the credential). Credential ENTRY lives in the Connections
  // catalog, exactly like connector secrets. (state declared near connectors.)
  const mcpCatalogById = useMemo(() => {
    const m = {};
    for (const r of mcpCatalog) m[r.id] = r;
    return m;
  }, [mcpCatalog]);
  // Resolve a pin's chip label from the configured credential rows, preferring
  // the ROOM's own scope. A pin carries only {mcpId, instanceId}; instanceId
  // 'default' collides across scopes (the network's github AND the room's github
  // both live at 'default'), so a naive first-match would show the network's
  // generic label. We match (mcpId, instanceId) and pick the most-specific scope
  // — POOL ▸ NETWORK ▸ TENANT — so a room-scoped pin reads "kyn GitHub", not the
  // inherited network name. Falls back to the registry display name.
  const mcpPinLabel = useCallback((mcpId, instanceId) => {
    const iid = instanceId || 'default';
    const rank = { POOL: 3, NETWORK: 2, TENANT: 1 };
    let best = null;
    let bestRank = -1;
    for (const r of mcpCredRows) {
      if (r.mcpId !== mcpId) continue;
      if ((r.instanceId || 'default') !== iid) continue;
      const rk = rank[String(r.scope?.kind || '').toUpperCase()] || 0;
      if (rk > bestRank) { best = r; bestRank = rk; }
    }
    if (best && best.label) return best.label;
    const reg = mcpCatalogById[mcpId] || {};
    return reg.displayName || mcpId;
  }, [mcpCredRows, mcpCatalogById]);
  // Loader for AgentToolPicker: configured MCP instances visible to this room,
  // grouped by mcpId. Pulled from the Connections catalog list.
  //
  // CANONICALIZE by (mcpId, instanceId) so each pin resolves to exactly one row —
  // see groupMcpInstancesByScope. This feeds attach, label, edit, and the
  // attached-check identically, so the room's own POOL shell shadows the
  // inherited NETWORK default everywhere at once.
  const mcpInstancesLoader = useCallback(async () => {
    const { mcpCredentials } = await listMcpCredentials({
      networkId: networkId || undefined,
      poolId: poolId || undefined,
    });
    return groupMcpInstancesByScope(mcpCredentials, { poolScoreable: !!poolId });
  }, [networkId, poolId]);
  const toggleSettingsSection = useCallback((key) => {
    setSettingsSectionOpen((s) => ({ ...s, [key]: !s[key] }));
  }, []);

  // Room-context injection override — tri-state: inherit (no row) / on / off.
  // The room owner's switch that beats the network's snapshotted setting.
  const [roomCtxOverride, setRoomCtxOverride] = useState(null); // 'on' | 'off' | null(inherit)
  const [roomCtxLoading, setRoomCtxLoading] = useState(false);
  const [roomCtxBusy, setRoomCtxBusy] = useState(false);
  const [roomCtxError, setRoomCtxError] = useState(null);

  // Objective-judge model (SCHEDULER.md §11.1b) — '' means default tier.
  const [judgeModel, setJudgeModel] = useState('');
  const [judgeModelBusy, setJudgeModelBusy] = useState(false);

  const loadRoomCtxOverride = useCallback(async () => {
    if (!networkId || !poolId) return;
    setRoomCtxLoading(true);
    setRoomCtxError(null);
    try {
      const res = await getRoomContextOverride(networkId, poolId);
      const m = (res && (res.mode === 'on' || res.mode === 'off')) ? res.mode : null;
      setRoomCtxOverride(m);
      setJudgeModel(res && typeof res.judgeModel === 'string' ? res.judgeModel : '');
    } catch (e) {
      setRoomCtxError(e?.message || 'Failed to load room-context setting');
    } finally {
      setRoomCtxLoading(false);
    }
  }, [networkId, poolId]);

  const applyJudgeModel = useCallback(async (next) => {
    if (!networkId || !poolId || judgeModelBusy) return;
    setJudgeModelBusy(true);
    setRoomCtxError(null);
    try {
      await setPoolJudgeModel(networkId, poolId, next);  // '' clears → default
      setJudgeModel(next);
    } catch (e) {
      setRoomCtxError(e?.message || 'Failed to update judge model');
    } finally {
      setJudgeModelBusy(false);
    }
  }, [networkId, poolId, judgeModelBusy]);

  const applyRoomCtxOverride = useCallback(async (next) => {
    // next ∈ 'inherit' | 'on' | 'off'
    if (!networkId || !poolId || roomCtxBusy) return;
    setRoomCtxBusy(true);
    setRoomCtxError(null);
    try {
      if (next === 'inherit') {
        await clearRoomContextOverride(networkId, poolId);
        setRoomCtxOverride(null);
      } else {
        await setRoomContextOverride(networkId, poolId, next);
        setRoomCtxOverride(next);
      }
    } catch (e) {
      setRoomCtxError(e?.message || 'Failed to update room-context setting');
    } finally {
      setRoomCtxBusy(false);
    }
  }, [networkId, poolId, roomCtxBusy]);

  useEffect(() => {
    if (open && settingsSectionOpen.roomContext) loadRoomCtxOverride();
  }, [open, settingsSectionOpen.roomContext, loadRoomCtxOverride]);
  const lastEventIdRef = useRef(null);
  const lastAgentSupEventIdRef = useRef(null);
  const lastConnectorEventIdRef = useRef(null);
  // Parent may pass new inline fns each render. ``refresh`` is in a mount effect +
  // event-driven ``refresh`` deps — keep callback refs so we don't refetch
  // ``GET .../members`` on every re-render.
  const onTriggerModeChangeRef = useRef(onTriggerModeChange);
  useEffect(() => {
    onTriggerModeChangeRef.current = onTriggerModeChange;
  }, [onTriggerModeChange]);
  const onRosterChangeRef = useRef(onRosterChange);
  useEffect(() => {
    onRosterChangeRef.current = onRosterChange;
  }, [onRosterChange]);

  const currentTrigger = normalizeTriggerMode(triggerMode);

  const poolWideConnectorToolNames = useMemo(() => {
    const s = new Set([
      ...Object.keys(connectorChoices || {}),
      ...Object.keys(poolWideConnectorOverrides || {}),
    ]);
    (agentConnectorAgents || []).forEach((a) => {
      (a.tools || []).forEach((t) => {
        if (t.toolName) s.add(t.toolName);
      });
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [connectorChoices, poolWideConnectorOverrides, agentConnectorAgents]);

  const refresh = useCallback(async () => {
    if (!networkId || !poolId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listPoolMembers(networkId, poolId);
      setMembers(Array.isArray(data.members) ? data.members : []);
      // The members endpoint also returns the pool row with its current
      // trigger mode (Phase B) — propagate to the parent so the prompt
      // box gating refreshes if the drawer was the first read.
      const tm = data?.pool?.triggerMode;
      if (tm && typeof onTriggerModeChangeRef.current === 'function') {
        onTriggerModeChangeRef.current(normalizeTriggerMode(tm));
      }
      // Hydrate builder execution mode from persisted pool setting
      if (data?.builderExecutionMode) {
        setBuilderMode(data.builderExecutionMode);
      }
    } catch (e) {
      setError(e.message || 'Failed to load pool members');
    } finally {
      setLoading(false);
    }
  }, [networkId, poolId]);

  const handleTriggerChange = useCallback(
    async (nextMode) => {
      const next = normalizeTriggerMode(nextMode);
      if (!networkId || !poolId) return;
      if (next === currentTrigger) return;
      setTriggerBusy(true);
      setTriggerMsg(null);
      try {
        const res = await updatePoolSettings(networkId, poolId, { trigger: next });
        const persisted = normalizeTriggerMode(res?.pool?.triggerMode || next);
        if (typeof onTriggerModeChangeRef.current === 'function') {
          onTriggerModeChangeRef.current(persisted);
        }
        setTriggerMsg({
          kind: 'ok',
          text: res?.changed === false
            ? `Trigger mode is already ${persisted}.`
            : `Trigger mode set to ${persisted}.`,
        });
      } catch (ex) {
        setTriggerMsg({ kind: 'error', text: ex.message || 'Update failed' });
      } finally {
        setTriggerBusy(false);
      }
    },
    [networkId, poolId, currentTrigger],
  );

  const handleBuilderModeChange = useCallback(
    async (nextMode) => {
      if (!networkId || !poolId) return;
      setBuilderModeBusy(true);
      setBuilderModeMsg(null);
      try {
        await updatePoolSettings(networkId, poolId, { builderExecutionMode: nextMode });
        setBuilderMode(nextMode);
        setBuilderModeMsg({
          kind: 'ok',
          text: nextMode === 'inherit'
            ? 'Reset to network default.'
            : `Builder mode set to ${nextMode === 'interactive' ? 'Interactive' : 'Autonomous'}.`,
        });
      } catch (ex) {
        setBuilderModeMsg({ kind: 'error', text: ex.message || 'Update failed' });
      } finally {
        setBuilderModeBusy(false);
      }
    },
    [networkId, poolId],
  );

  // Phase C — load the agent-supervisor matrix (workbench default + any
  // pool override) when the drawer opens. We deliberately don't preload
  // this on first mount because the API lists *all* agents in the
  // released ASL and we want to keep the room's first-paint payload
  // small. The drawer is the only place this data is rendered.
  const refreshAgentSupervisors = useCallback(async () => {
    if (!networkId || !poolId) return;
    setAgentSupLoading(true);
    setAgentSupError(null);
    try {
      const data = await listPoolAgentSupervisors(networkId, poolId);
      setAgentSupervisors(realAgentsOnly(data?.agents));
    } catch (e) {
      setAgentSupError(e.message || 'Failed to load agent supervisors');
    } finally {
      setAgentSupLoading(false);
    }
  }, [networkId, poolId]);

  const handleSetSupervisor = useCallback(
    async (agentId, emailsList) => {
      const emails = [...new Set((emailsList || []).map((e) => String(e || '').trim().toLowerCase()).filter(Boolean))];
      if (!emails.length) {
        setOverrideMsg({
          agentId,
          kind: 'error',
          text: 'Pick at least one workspace supervisor.',
        });
        return;
      }
      if (!networkId || !poolId || !agentId) return;
      setOverrideBusyId(agentId);
      setOverrideMsg(null);
      try {
        await setPoolAgentSupervisor(networkId, poolId, agentId, { emails });
        setAgentSupervisors((prev) =>
          prev.map((row) =>
            row.agentId === agentId
              ? {
                  ...row,
                  source: 'OVERRIDE',
                  effectiveSupervisorEmails: emails,
                  effectiveSupervisorEmail: emails[0],
                  effectiveSupervisor: {
                    ...(row.effectiveSupervisor && typeof row.effectiveSupervisor === 'object'
                      ? row.effectiveSupervisor
                      : {}),
                    email: emails[0],
                  },
                }
              : row,
          ),
        );
        setOverrideMsg({
          agentId,
          kind: 'ok',
          text:
            emails.length === 1
              ? `Supervisor set to ${emails[0]}.`
              : `Supervisors set (${emails.length} inboxes).`,
        });
        setEditingAgentId(null);
        setOverrideDraftEmails([]);
      } catch (ex) {
        setOverrideMsg({
          agentId,
          kind: 'error',
          text: ex.message || 'Set supervisor failed',
        });
      } finally {
        setOverrideBusyId(null);
      }
    },
    [networkId, poolId],
  );

  // Phase D — connector overrides. Load both the override map and the
  // pool-aware secrets registry chain in parallel: the picker dropdown
  // for any tool is the chain (TENANT ∪ NETWORK ∪ this POOL), the
  // current "pinned" value is the override row.
  const refreshConnectorOverrides = useCallback(async () => {
    if (!networkId || !poolId) return;
    setConnectorLoading(true);
    setConnectorError(null);
    try {
      const [matrixRes, overridesRes, mcpReg, mcpOv, wsOv, mcpCreds] = await Promise.all([
        listAgentConnectorOverrides(networkId, poolId),
        listPoolConnectorOverrides(networkId, poolId),
        listMcpRegistry().catch(() => ({ mcps: [] })),
        listAgentMcpOverrides(networkId, poolId).catch(() => ({ overrides: [] })),
        listAgentWorkspaceOverrides(networkId, poolId).catch(() => ({ overrides: [] })),
        listMcpCredentials({ networkId, poolId }).catch(() => ({ mcpCredentials: [] })),
      ]);
      setWorkspaceOverrides(Array.isArray(wsOv?.overrides) ? wsOv.overrides : []);
      setMcpCredRows(Array.isArray(mcpCreds?.mcpCredentials) ? mcpCreds.mcpCredentials : []);
      setAgentConnectorAgents(realAgentsOnly(matrixRes?.agents));
      // MCP catalog (authenticated only) + existing per-agent pins, folded into
      // the same Tools picker.
      // FULL MCP catalog (incl. unauthenticated) so the room's tool picker
      // shows the same list + counts as Connections / Workbench. The picker
      // routes unauth MCPs to a one-click enable, auth MCPs to the instance pin.
      setMcpCatalog(Array.isArray(mcpReg?.mcps) ? mcpReg.mcps : []);
      setMcpOverrides(Array.isArray(mcpOv?.overrides) ? mcpOv.overrides : []);
      setMcpFrozen(Array.isArray(mcpOv?.frozen) ? mcpOv.frozen : []);
      const byTool = overridesRes?.overridesByTool || {};
      setPoolWideConnectorOverrides({ ...byTool });
      let list = [];
      try {
        const r = await listSecretsRegistry({
          networkId,
          poolId,
        });
        list = Array.isArray(r?.secrets) ? r.secrets : [];
      } catch (regErr) {
        console.warn('[PoolSettingsDrawer] pool registry chain listing failed:', regErr);
      }
      const choices = {};
      const workspaces = [];
      for (let i = 0; i < list.length; i += 1) {
        const row = list[i];
        const tn = String(row?.toolName || '').trim();
        if (!tn) continue;
        if (tn === 'workspace') {
          // Shared workspaces are pinned via a dedicated single-select (one
          // workspace per agent), not the connector secretId map.
          workspaces.push(row);
          continue;
        }
        // Skills share this registry but are NOT connectors (own Skills tab /
        // workspace-bake flow) — never offer them as connector instances.
        if (tn === 'skill') continue;
        if (!choices[tn]) choices[tn] = [];
        choices[tn].push(row);
      }
      setConnectorChoices(choices);
      setWorkspaceChoices(workspaces);
    } catch (e) {
      setConnectorError(e.message || 'Failed to load connector overrides');
    } finally {
      setConnectorLoading(false);
    }
  }, [networkId, poolId]);

  const handleSetAgentConnector = useCallback(
    async (agentId, toolName, secretId) => {
      const sid = (secretId || '').trim();
      if (!sid) {
        setConnectorMsg({
          scope: 'agent',
          agentId,
          toolName,
          kind: 'error',
          text: 'Pick a connection from the registry.',
        });
        return;
      }
      if (!networkId || !poolId || !agentId || !toolName) return;
      setConnectorBusyKey(`agent:${agentId}:${toolName}`);
      setConnectorMsg(null);
      try {
        const res = await setAgentPoolConnectorOverride(
          networkId,
          poolId,
          agentId,
          toolName,
          { secretId: sid },
        );
        const ovr = res?.override;
        setAgentConnectorAgents((prev) =>
          (prev || []).map((row) => {
            if (row.agentId !== agentId) return row;
            return {
              ...row,
              tools: (row.tools || []).map((t) =>
                t.toolName === toolName
                  ? {
                      ...t,
                      effectiveSecretId: sid,
                      source: 'OVERRIDE',
                      override:
                        ovr ||
                        t.override ||
                        {
                          toolName,
                          secretId: sid,
                          agentId,
                          poolId,
                          networkId,
                        },
                    }
                  : t,
              ),
            };
          }),
        );
        setConnectorMsg({
          scope: 'agent',
          agentId,
          toolName,
          kind: 'ok',
          text: res?.changed === false
            ? 'Connector already pinned for this agent.'
            : 'Connector pinned for this agent.',
        });
      } catch (ex) {
        const reason = ex?.data?.reason;
        const friendly = {
          not_found: 'That connection no longer exists in the registry.',
          tool_mismatch: 'That secret belongs to a different tool.',
          scope_pool_mismatch: 'That secret is scoped to another pool — pick one visible to this pool.',
          scope_network_mismatch: 'That secret is scoped to another network.',
        }[reason];
        setConnectorMsg({
          scope: 'agent',
          agentId,
          toolName,
          kind: 'error',
          text: friendly || ex.message || 'Pin failed',
        });
      } finally {
        setConnectorBusyKey(null);
      }
    },
    [networkId, poolId],
  );

  const handleClearAgentConnector = useCallback(
    async (agentId, toolName) => {
      if (!networkId || !poolId || !agentId || !toolName) return;
      if (!window.confirm(
        `Clear per-agent override for ${toolName} on this agent? Falls back to pool-wide, then network default.`,
      )) {
        return;
      }
      setConnectorBusyKey(`agent:${agentId}:${toolName}`);
      setConnectorMsg(null);
      try {
        await clearAgentPoolConnectorOverride(networkId, poolId, agentId, toolName);
        await refreshConnectorOverrides();
        setConnectorMsg({
          scope: 'agent',
          agentId,
          toolName,
          kind: 'ok',
          text: 'Reverted to pool-wide / network default.',
        });
      } catch (ex) {
        setConnectorMsg({
          scope: 'agent',
          agentId,
          toolName,
          kind: 'error',
          text: ex.message || 'Clear failed',
        });
      } finally {
        setConnectorBusyKey(null);
      }
    },
    [networkId, poolId, refreshConnectorOverrides],
  );

  // Diff the picker's draft against the per-agent overrides currently in
  // DDB and fire set / clear calls per change. Bare-string entries (tool
  // selected but no secret pinned) are ignored at the pool level — a
  // pool override only makes sense when the user picked a registry
  // secret. Errors are partial-failure tolerant: we report the first
  // error in ``connectorMsg`` and refresh from the server so the picker
  // reflects whatever did persist.
  const handleSaveAgentConnectors = useCallback(
    async () => {
      if (!networkId || !poolId || !selectedConnectorAgentId) return;
      const aid = selectedConnectorAgentId;
      const roomWide = aid === ROOM_WIDE_AGENT_ID;
      const want = new Map(); // toolName → secretId
      for (const e of connectorAgentDraft) {
        if (typeof e === 'string') continue; // bare strings — no pin
        const tn = String(e?.name || '').trim();
        const sid = String(e?.secretId || '').trim();
        if (!tn || !sid) continue;
        want.set(tn, sid);
      }
      const have = new Map();
      for (const e of connectorAgentBaseline) {
        const tn = String(e?.name || '').trim();
        const sid = String(e?.secretId || '').trim();
        if (tn && sid) have.set(tn, sid);
      }
      const toSet = [];
      for (const [tn, sid] of want.entries()) {
        if (have.get(tn) !== sid) toSet.push([tn, sid]);
      }
      const toClear = [];
      for (const tn of have.keys()) {
        if (!want.has(tn)) toClear.push(tn);
      }
      // MCP pin diff (same shape: mcpId → instanceId). MCP entries live in the
      // SAME picker draft via the mcp* props, tracked separately here.
      // A room PIN points at a configured CREDENTIAL instance — only meaningful
      // for AUTHENTICATED MCPs. Unauthenticated MCPs (fetch, time, …) have no
      // credential to pin; they're enabled at build time (Workbench), not pinned
      // here. They're visible in the shared picker for a consistent catalog, but
      // we skip writing a pin for them (the backend would reject "not configured").
      const isPinnable = (mid) => {
        const reg = mcpCatalogById[mid];
        return !reg || reg.authRequired; // default to pinnable if unknown
      };
      const mcpWant = new Map();
      for (const e of mcpPinDraft) {
        const mid = String(e?.id || '').trim();
        const iid = String(e?.instanceId || 'default').trim() || 'default';
        if (mid && isPinnable(mid)) mcpWant.set(mid, iid);
      }
      const mcpHave = new Map();
      for (const e of mcpPinBaseline) {
        const mid = String(e?.id || '').trim();
        const iid = String(e?.instanceId || 'default').trim() || 'default';
        if (mid && isPinnable(mid)) mcpHave.set(mid, iid);
      }
      const mcpToSet = [];
      for (const [mid, iid] of mcpWant.entries()) {
        if (mcpHave.get(mid) !== iid) mcpToSet.push([mid, iid]);
      }
      const mcpToClear = [];
      for (const mid of mcpHave.keys()) {
        if (!mcpWant.has(mid)) mcpToClear.push(mid);
      }

      // Workspace pin diff (single workspaceId for the room). A shared workspace
      // is pinned ROOM-WIDE — every agent collaborates on the one tree (the
      // runtime lease serialises writers). Only the Room-Wide scope offers it;
      // per-agent picks don't carry a workspace.
      const wsWant = roomWide ? String(workspacePinDraft || '').trim() : '';
      const wsHave = roomWide ? String(workspacePinBaseline || '').trim() : '';
      const wsChanged = roomWide && wsWant !== wsHave;

      if (toSet.length === 0 && toClear.length === 0
          && mcpToSet.length === 0 && mcpToClear.length === 0 && !wsChanged) {
        setConnectorMsg({ scope: 'agent', agentId: aid, kind: 'ok', text: 'No changes to save.' });
        return;
      }
      setConnectorAgentBusy(true);
      setConnectorMsg(null);
      let firstErr = null;
      // Room Wide writes the pool-wide rows (no agentId); per-agent writes the
      // agent rows. Same diff, different endpoint family.
      for (const [tn, sid] of toSet) {
        try {
          if (roomWide) await setPoolConnectorOverride(networkId, poolId, tn, { secretId: sid });
          else await setAgentPoolConnectorOverride(networkId, poolId, aid, tn, { secretId: sid });
        } catch (ex) {
          if (!firstErr) firstErr = ex;
        }
      }
      for (const tn of toClear) {
        try {
          if (roomWide) await clearPoolConnectorOverride(networkId, poolId, tn);
          else await clearAgentPoolConnectorOverride(networkId, poolId, aid, tn);
        } catch (ex) {
          if (!firstErr) firstErr = ex;
        }
      }
      for (const [mid, iid] of mcpToSet) {
        try {
          if (roomWide) await setPoolMcpInstanceOverride(networkId, poolId, mid, iid);
          else await setAgentMcpInstanceOverride(networkId, poolId, aid, mid, iid);
        } catch (ex) {
          if (!firstErr) firstErr = ex;
        }
      }
      for (const mid of mcpToClear) {
        try {
          if (roomWide) await clearPoolMcpInstanceOverride(networkId, poolId, mid);
          else await clearAgentMcpInstanceOverride(networkId, poolId, aid, mid);
        } catch (ex) {
          if (!firstErr) firstErr = ex;
        }
      }
      // Workspace pin (room-wide / pool-wide): set the new one, then clear the
      // old if it differs. Two calls only when both are set and differ.
      if (wsChanged) {
        try {
          if (wsWant) {
            await setPoolWorkspaceOverride(networkId, poolId, wsWant);
            if (wsHave && wsHave !== wsWant) {
              await clearPoolWorkspaceOverride(networkId, poolId, wsHave);
            }
          } else if (wsHave) {
            await clearPoolWorkspaceOverride(networkId, poolId, wsHave);
          }
        } catch (ex) {
          if (!firstErr) firstErr = ex;
        }
      }
      try {
        await refreshConnectorOverrides();
      } catch (_) { /* ignore — UI will retry on next open */ }
      if (firstErr) {
        const reason = firstErr?.data?.reason;
        const friendly = {
          not_found: 'A connection no longer exists in the registry.',
          tool_mismatch: 'A secret belongs to a different tool.',
          scope_pool_mismatch: 'A secret is scoped to another pool — pick one visible to this pool.',
          scope_network_mismatch: 'A secret is scoped to another network.',
        }[reason];
        setConnectorMsg({
          scope: 'agent',
          agentId: aid,
          kind: 'error',
          text: friendly || firstErr.message || 'Save failed (some changes may have applied).',
        });
      } else {
        setConnectorMsg({
          scope: 'agent',
          agentId: aid,
          kind: 'ok',
          text:
            toSet.length && toClear.length
              ? `Saved: ${toSet.length} pinned, ${toClear.length} cleared.`
              : toSet.length
              ? `Pinned ${toSet.length} connector${toSet.length === 1 ? '' : 's'}.`
              : `Cleared ${toClear.length} override${toClear.length === 1 ? '' : 's'}.`,
        });
      }
      setConnectorAgentBusy(false);
    },
    [
      networkId, poolId, selectedConnectorAgentId,
      connectorAgentDraft, connectorAgentBaseline,
      mcpPinDraft, mcpPinBaseline,
      workspacePinDraft, workspacePinBaseline,
      refreshConnectorOverrides,
    ],
  );

  const handleResetAgentConnectorsDraft = useCallback(() => {
    setConnectorAgentDraft(connectorAgentBaseline);
    setMcpPinDraft(mcpPinBaseline);
    setWorkspacePinDraft(workspacePinBaseline);
  }, [connectorAgentBaseline, mcpPinBaseline, workspacePinBaseline]);

  // Seed the selected agent's MCP pins (AgentToolPicker MCP entries
  // {id, instanceId, label}) from the room's override rows — parallels the
  // connector baseline/draft seeding below.
  useEffect(() => {
    if (!selectedConnectorAgentId) {
      setMcpPinBaseline([]);
      setMcpPinDraft([]);
      return;
    }
    const isRoomWide = selectedConnectorAgentId === ROOM_WIDE_AGENT_ID;
    // Room Wide → pool-wide pins only (rows with NO agentId). Per-agent → that
    // agent's FROZEN MCPs (inherited from the release) as the base, with the
    // agent's pins overriding the instance — mirrors how connectors show their
    // frozen stamps plus room overrides. Same chip shape either way.
    const pins = isRoomWide
      ? mcpOverrides.filter((o) => !o.agentId)
      : mcpOverrides.filter((o) => o.agentId === selectedConnectorAgentId);
    const byMcp = new Map(); // mcpId → { id, instanceId, label }
    const chip = (mcpId, instanceId, label) => {
      // Prefer an explicit label carried on the row (frozen entries have one);
      // otherwise resolve from the credential rows preferring the ROOM's own
      // scope so a pin reads "kyn GitHub", not the inherited network name.
      return {
        id: mcpId,
        instanceId: instanceId || 'default',
        label: label || mcpPinLabel(mcpId, instanceId),
      };
    };
    if (!isRoomWide) {
      // Inherited (frozen) MCPs for this agent first.
      for (const f of mcpFrozen) {
        if (f.agentId !== selectedConnectorAgentId) continue;
        byMcp.set(f.mcpId, chip(f.mcpId, f.instanceId, f.label));
      }
    }
    // Pins override / add.
    for (const o of pins) {
      byMcp.set(o.mcpId, chip(o.mcpId, o.instanceId, o.label));
    }
    const baseline = Array.from(byMcp.values());
    setMcpPinBaseline(baseline);
    setMcpPinDraft(baseline);
  }, [selectedConnectorAgentId, mcpOverrides, mcpFrozen, mcpPinLabel]);

  // Seed the selected agent's WORKSPACE pin (single workspaceId) from the
  // override rows. Per-agent pin wins; else a pool-wide (no agentId) pin; else none.
  useEffect(() => {
    if (!selectedConnectorAgentId) {
      setWorkspacePinBaseline('');
      setWorkspacePinDraft('');
      return;
    }
    const isRoomWide = selectedConnectorAgentId === ROOM_WIDE_AGENT_ID;
    let pinned = '';
    if (isRoomWide) {
      const pw = workspaceOverrides.find((o) => !o.agentId);
      pinned = pw?.workspaceId || '';
    } else {
      const own = workspaceOverrides.find((o) => o.agentId === selectedConnectorAgentId);
      pinned = own?.workspaceId || (workspaceOverrides.find((o) => !o.agentId)?.workspaceId || '');
    }
    setWorkspacePinBaseline(pinned);
    setWorkspacePinDraft(pinned);
  }, [selectedConnectorAgentId, workspaceOverrides]);

  const handleSetPoolWideConnector = useCallback(
    async (toolName, secretId) => {
      const sid = (secretId || '').trim();
      if (!sid) {
        setConnectorMsg({
          scope: 'pool',
          toolName,
          kind: 'error',
          text: 'Pick a connection from the registry.',
        });
        return;
      }
      if (!networkId || !poolId || !toolName) return;
      setConnectorBusyKey(`pool:${toolName}`);
      setConnectorMsg(null);
      try {
        const res = await setPoolConnectorOverride(networkId, poolId, toolName, {
          secretId: sid,
        });
        setPoolWideConnectorOverrides((prev) => ({
          ...prev,
          [toolName]: res?.override || {
            toolName,
            secretId: sid,
            poolId,
            networkId,
          },
        }));
        await refreshConnectorOverrides();
        setConnectorMsg({
          scope: 'pool',
          toolName,
          kind: 'ok',
          text: res?.changed === false
            ? 'Connector already pinned.'
            : 'Pool-wide connector pinned.',
        });
      } catch (ex) {
        const reason = ex?.data?.reason;
        const friendly = {
          not_found: 'That connection no longer exists in the registry.',
          tool_mismatch: 'That secret belongs to a different tool.',
          scope_pool_mismatch: 'That secret is scoped to another pool — pick one visible to this pool.',
          scope_network_mismatch: 'That secret is scoped to another network.',
        }[reason];
        setConnectorMsg({
          scope: 'pool',
          toolName,
          kind: 'error',
          text: friendly || ex.message || 'Pin failed',
        });
      } finally {
        setConnectorBusyKey(null);
      }
    },
    [networkId, poolId, refreshConnectorOverrides],
  );

  const handleClearPoolWideConnector = useCallback(
    async (toolName) => {
      if (!networkId || !poolId || !toolName) return;
      if (!window.confirm(
        `Clear pool-wide override for ${toolName}? Agents without a per-agent pin fall back to the network default.`,
      )) {
        return;
      }
      setConnectorBusyKey(`pool:${toolName}`);
      setConnectorMsg(null);
      try {
        await clearPoolConnectorOverride(networkId, poolId, toolName);
        setPoolWideConnectorOverrides((prev) => {
          const next = { ...prev };
          delete next[toolName];
          return next;
        });
        await refreshConnectorOverrides();
        setConnectorMsg({
          scope: 'pool',
          toolName,
          kind: 'ok',
          text: 'Pool-wide override cleared.',
        });
      } catch (ex) {
        setConnectorMsg({
          scope: 'pool',
          toolName,
          kind: 'error',
          text: ex.message || 'Clear failed',
        });
      } finally {
        setConnectorBusyKey(null);
      }
    },
    [networkId, poolId, refreshConnectorOverrides],
  );

  const handleClearSupervisor = useCallback(
    async (agentId, defaultEmail) => {
      if (!networkId || !poolId || !agentId) return;
      const promptText = defaultEmail
        ? `Reset supervisor to workbench default (${defaultEmail})?`
        : 'Reset supervisor to the workbench default?';
      if (!window.confirm(promptText)) return;
      setOverrideBusyId(agentId);
      setOverrideMsg(null);
      try {
        await clearPoolAgentSupervisor(networkId, poolId, agentId);
        setAgentSupervisors((prev) =>
          prev.map((row) =>
            row.agentId === agentId
              ? {
                  ...row,
                  source: 'WORKBENCH',
                  effectiveSupervisorEmails: defaultEmail ? [defaultEmail] : [],
                  effectiveSupervisorEmail: defaultEmail || '',
                  effectiveSupervisor: defaultEmail
                    ? { ...(row.effectiveSupervisor || {}), email: defaultEmail }
                    : row.effectiveSupervisor,
                }
              : row,
          ),
        );
        setOverrideMsg({
          agentId,
          kind: 'ok',
          text: 'Reverted to workbench default.',
        });
      } catch (ex) {
        setOverrideMsg({
          agentId,
          kind: 'error',
          text: ex.message || 'Reset failed',
        });
      } finally {
        setOverrideBusyId(null);
      }
    },
    [networkId, poolId],
  );

  // Initial roster fetch happens regardless of ``open`` so the parent can
  // render a "Members · N" count badge in the room header before a user
  // has clicked the drawer open. The REST call is cheap (pool partitions
  // are tiny) and the result is reused when the drawer actually opens.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Lazy-load the agent-supervisor matrix the first time the drawer is
  // opened (and on every subsequent open, in case another operator
  // changed something while the drawer was closed).
  useEffect(() => {
    if (!open) return;
    refreshAgentSupervisors();
    refreshConnectorOverrides();
    // Reset transient editor UI whenever the drawer reopens — stale
    // half-typed emails from a previous session are confusing.
    setEditingAgentId(null);
    setOverrideDraftEmails([]);
    setOverrideMsg(null);
    setConnectorMsg(null);
  }, [open, refreshAgentSupervisors, refreshConnectorOverrides]);

  // Tools catalog — same source the Workbench Agents tab uses for the
  // "Tools & Routing" picker. We mount it once when the drawer opens so
  // the per-agent connector picker has its option list ready by the
  // time the user clicks an agent.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setConnectorToolsCatalog((prev) => ({ ...prev, loading: true, error: '' }));
    listTools({ forAgent: true })
      .then((data) => {
        if (cancelled) return;
        setConnectorToolsCatalog({
          tools: Array.isArray(data?.tools) ? data.tools : [],
          loading: false,
          error: '',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setConnectorToolsCatalog({
          tools: [],
          loading: false,
          error: err?.message || 'Failed to load tools catalog',
        });
      });
    return () => { cancelled = true; };
  }, [open]);

  // Default the agent dropdown to the first agent the matrix returned;
  // re-default if the selection points at an agent that no longer
  // exists (e.g. release switched while the drawer was open).
  useEffect(() => {
    // "Room Wide" is always a valid selection (pool-scoped, no agent needed),
    // so it survives roster changes and is the default when there are no agents.
    if (selectedConnectorAgentId === ROOM_WIDE_AGENT_ID) return;
    if (!agentConnectorAgents.length) {
      if (selectedConnectorAgentId) setSelectedConnectorAgentId(ROOM_WIDE_AGENT_ID);
      return;
    }
    const ids = agentConnectorAgents.map((a) => a.agentId);
    if (!ids.includes(selectedConnectorAgentId)) {
      setSelectedConnectorAgentId(ids[0]);
    }
  }, [agentConnectorAgents, selectedConnectorAgentId]);

  // Whenever the agent or matrix changes, derive the baseline list of
  // per-agent OVERRIDE pins for that agent and seed the picker draft to
  // match. The picker shape mirrors the Workbench Tools & Routing field:
  // an array where each entry is either a bare tool name (no pin) or
  // ``{ name, secretId, secretName }``. We only emit object entries
  // here because every baseline pin has a secretId by definition.
  useEffect(() => {
    if (!selectedConnectorAgentId) {
      setConnectorAgentBaseline([]);
      setConnectorAgentDraft([]);
      return;
    }
    // Room Wide → seed from the pool-wide override rows (keyed by tool); the
    // chip shape matches per-agent so Save/diff is identical.
    if (selectedConnectorAgentId === ROOM_WIDE_AGENT_ID) {
      const baseline = [];
      const byTool = poolWideConnectorOverrides || {};
      for (const tn of Object.keys(byTool)) {
        const sid = String(byTool[tn]?.secretId || '').trim();
        if (!sid) continue;
        const choices = connectorChoices[tn] || [];
        const match = choices.find((c) => (c?.secretId || '') === sid);
        const secretName = match?.secretName || '';
        baseline.push({
          name: tn,
          instanceLabel: secretName || sid.slice(0, 8),
          secretId: sid,
          secretName,
        });
      }
      setConnectorAgentBaseline(baseline);
      setConnectorAgentDraft(baseline);
      return;
    }
    const row = agentConnectorAgents.find((a) => a.agentId === selectedConnectorAgentId);
    const tools = Array.isArray(row?.tools) ? row.tools : [];
    const baseline = [];
    for (const t of tools) {
      // Seed the chip from a per-agent pool OVERRIDE *or* the connector the
      // network admin assigned to this agent at build time (``AGENT`` —
      // frozen into the release). Both have a real effectiveSecretId, so
      // both should show as a chip; without ``AGENT`` an inherited connector
      // like ``comfy`` on BuilderAgent was invisible in the room. Seeding it
      // into BOTH baseline and draft makes Save a no-op unless the room
      // owner actually changes it (then it becomes a real override).
      const _src = (t?.source || '').toUpperCase();
      if (_src !== 'OVERRIDE' && _src !== 'AGENT') continue;
      const sid = String(t?.effectiveSecretId || '').trim();
      if (!sid) continue;
      const choices = connectorChoices[t.toolName] || [];
      const match = choices.find((c) => (c?.secretId || '') === sid);
      const secretName = match?.secretName || '';
      // ``instanceLabel`` is the chip headline. Mirror the
      // ToolCodeConnectorAgentConfigWizard convention so existing
      // overrides render the same way as freshly-picked ones:
      // ``<secretName> · <toolName>`` (e.g. "github-prod · code-connector").
      // Falls back to a short secret-id prefix when the registry
      // lookup misses (e.g. secret deleted but override still set).
      const instanceLabel = secretName || sid.slice(0, 8);
      baseline.push({
        name: t.toolName,
        instanceLabel,
        secretId: sid,
        secretName,
      });
    }
    setConnectorAgentBaseline(baseline);
    setConnectorAgentDraft(baseline);
  }, [selectedConnectorAgentId, agentConnectorAgents, connectorChoices, poolWideConnectorOverrides]);

  // Same idea for Agent supervisors: default the selected agent to the
  // first row, and seed the picker with whichever supervisor list is
  // effective (override if present, else workbench default).
  useEffect(() => {
    if (!agentSupervisors.length) {
      if (selectedSupervisorAgentId) setSelectedSupervisorAgentId('');
      return;
    }
    const ids = agentSupervisors.map((a) => a.agentId);
    if (!ids.includes(selectedSupervisorAgentId)) {
      setSelectedSupervisorAgentId(ids[0]);
    }
  }, [agentSupervisors, selectedSupervisorAgentId]);

  useEffect(() => {
    if (!selectedSupervisorAgentId) {
      setSupervisorDraftEmails([]);
      return;
    }
    const row = agentSupervisors.find((a) => a.agentId === selectedSupervisorAgentId);
    if (!row) {
      setSupervisorDraftEmails([]);
      return;
    }
    const list = Array.isArray(row.effectiveSupervisorEmails) && row.effectiveSupervisorEmails.length
      ? row.effectiveSupervisorEmails
      : (row.effectiveSupervisorEmail
          ? [row.effectiveSupervisorEmail]
          : (row.effectiveSupervisor?.email ? [row.effectiveSupervisor.email] : []));
    setSupervisorDraftEmails(list);
  }, [selectedSupervisorAgentId, agentSupervisors]);

  // Live refresh on membership events. We de-dupe by eventId so a single
  // mutation doesn't trigger N refetches when multiple tabs are listening.
  // Drop the open-gate too — the count needs to stay fresh in the header.
  useEffect(() => {
    if (!Array.isArray(channelEvents) || channelEvents.length === 0) return;
    const last = channelEvents[channelEvents.length - 1];
    const t = last?.eventType;
    const id = last?.eventId;
    if (!t || !id || id === lastEventIdRef.current) return;
    if (
      t === 'MEMBERSHIP_GRANTED' ||
      t === 'MEMBERSHIP_REVOKED' ||
      t === 'MEMBERSHIP_SELF_ELEVATED'
    ) {
      lastEventIdRef.current = id;
      refresh();
    } else if (t === 'POOL_SETTINGS_CHANGED') {
      // Trigger mode flipped by another operator (or another tab). Sync
      // the card and notify the parent without a full members refetch.
      lastEventIdRef.current = id;
      const tm = last?.payload?.settings?.triggerMode;
      if (tm && typeof onTriggerModeChangeRef.current === 'function') {
        onTriggerModeChangeRef.current(normalizeTriggerMode(tm));
      }
    }
  }, [channelEvents, refresh]);

  // Phase C — react to per-agent supervisor override events. We use a
  // separate de-dupe ref so a flurry of MEMBERSHIP events doesn't mask
  // a SUPERVISOR_CHANGED that happens to share an event index. We
  // *patch* in place from the payload when possible (cheap) and only
  // fall back to a full re-list if the payload is missing fields.
  useEffect(() => {
    if (!Array.isArray(channelEvents) || channelEvents.length === 0) return;
    const last = channelEvents[channelEvents.length - 1];
    if (last?.eventType !== 'POOL_AGENT_SUPERVISOR_CHANGED') return;
    const id = last?.eventId;
    if (!id || id === lastAgentSupEventIdRef.current) return;
    lastAgentSupEventIdRef.current = id;

    const payload = last?.payload || {};
    const agentId = payload.agentId;
    const action = (payload.action || 'SET').toUpperCase();
    if (!agentId) {
      // Defensive: re-list when we can't reconcile locally.
      refreshAgentSupervisors();
      return;
    }
    // Backend publishes ``CLEARED`` for resets and ``SET`` for adds /
    // replacements (see ``publish_pool_agent_supervisor_changed``).
    const isCleared = action === 'CLEARED' || action === 'CLEAR';
    setAgentSupervisors((prev) => {
      // If we haven't loaded the matrix yet (drawer never opened in
      // this session), there's nothing to patch — refreshAgentSupervisors
      // will pick up the change next time it opens.
      if (!prev || prev.length === 0) return prev;
      return prev.map((row) => {
        if (row.agentId !== agentId) return row;
        if (isCleared) {
          return {
            ...row,
            source: 'WORKBENCH',
            effectiveSupervisorEmails: row.workbenchDefaultEmail ? [row.workbenchDefaultEmail] : [],
            effectiveSupervisorEmail: row.workbenchDefaultEmail || '',
            effectiveSupervisor: {
              ...(row.effectiveSupervisor && typeof row.effectiveSupervisor === 'object'
                ? row.effectiveSupervisor
                : {}),
              email: row.workbenchDefaultEmail || null,
            },
          };
        }
        const plist = payload.supervisorEmails;
        const nextEmails = Array.isArray(plist) && plist.length
          ? plist
          : (payload.supervisorEmail ? [payload.supervisorEmail] : []);
        const primary = nextEmails[0] || payload.supervisorEmail || row.effectiveSupervisorEmail || '';
        return {
          ...row,
          source: 'OVERRIDE',
          effectiveSupervisorEmails: nextEmails.length ? nextEmails : (primary ? [primary] : []),
          effectiveSupervisorEmail: primary,
          effectiveSupervisor: {
            ...(row.effectiveSupervisor && typeof row.effectiveSupervisor === 'object'
              ? row.effectiveSupervisor
              : {}),
            email: primary,
          },
        };
      });
    });
  }, [channelEvents, refreshAgentSupervisors]);

  // Phase D — per-tool / per-agent connector override events. Without
  // ``agentId`` we patch pool-wide pins; with ``agentId`` we patch the matrix
  // row on SET, and re-fetch on CLEAR (effective fallback depends on pool-wide
  // + network default).
  useEffect(() => {
    if (!Array.isArray(channelEvents) || channelEvents.length === 0) return;
    const last = channelEvents[channelEvents.length - 1];
    if (last?.eventType !== 'POOL_CONNECTOR_OVERRIDE_CHANGED') return;
    const id = last?.eventId;
    if (!id || id === lastConnectorEventIdRef.current) return;
    lastConnectorEventIdRef.current = id;

    const payload = last?.payload || {};
    const toolName = payload.toolName;
    const agentId = payload.agentId;
    const action = (payload.action || 'SET').toUpperCase();
    if (!toolName) {
      refreshConnectorOverrides();
      return;
    }
    const isCleared = action === 'CLEARED' || action === 'CLEAR';

    if (agentId) {
      if (isCleared || !payload.secretId) {
        refreshConnectorOverrides();
        return;
      }
      const sid = String(payload.secretId || '').trim();
      setAgentConnectorAgents((prev) =>
        (prev || []).map((row) => {
          if (row.agentId !== agentId) return row;
          return {
            ...row,
            tools: (row.tools || []).map((t) =>
              t.toolName === toolName
                ? {
                    ...t,
                    effectiveSecretId: sid,
                    source: 'OVERRIDE',
                    override: {
                      ...(t.override && typeof t.override === 'object' ? t.override : {}),
                      toolName,
                      secretId: sid,
                      agentId,
                      networkId,
                      poolId,
                    },
                  }
                : t,
            ),
          };
        }),
      );
      return;
    }

    refreshConnectorOverrides();
  }, [channelEvents, networkId, poolId, refreshConnectorOverrides]);

  const activeMembers = useMemo(
    () =>
      (members || []).filter(
        (m) => (m.status || 'ACTIVE').toUpperCase() === 'ACTIVE',
      ),
    [members],
  );

  const activeCreatorCount = useMemo(
    () =>
      activeMembers.filter((m) => (m.role || '').toUpperCase() === 'CREATOR')
        .length,
    [activeMembers],
  );

  // Report active-roster size to the parent (for the header badge)
  // whenever the count changes. We skip the initial empty render so
  // the parent doesn't flash "Members · 0" for a frame before hydration.
  useEffect(() => {
    if (typeof onRosterChangeRef.current !== 'function') return;
    if (members == null) return;
    onRosterChangeRef.current(activeMembers.length);
  }, [activeMembers.length, members]);

  const handleInvite = async (e) => {
    e.preventDefault();
    const cleanEmail = (email || '').trim().toLowerCase();
    if (!cleanEmail) {
      setInviteMsg({ kind: 'error', text: 'Email is required.' });
      return;
    }
    setInviteBusy(true);
    setInviteMsg(null);
    try {
      const rolesToSend = selectedRoles.length > 0 ? selectedRoles : [role];
      const res = await addPoolMember(networkId, poolId, {
        email: cleanEmail,
        role: rolesToSend[0], // back-compat single-role field
        roles: rolesToSend,   // canonical multi-role array
      });
      const rolesLabel = rolesToSend.join(', ');
      setInviteMsg({
        kind: 'ok',
        text: res.wasExisting
          ? `Updated ${cleanEmail} → ${rolesLabel}.`
          : `Added ${cleanEmail} as ${rolesLabel}.`,
      });
      setEmail('');
      setSelectedRoles(['SUPERVISOR']);
      refresh();
    } catch (ex) {
      setInviteMsg({ kind: 'error', text: ex.message || 'Invite failed' });
    } finally {
      setInviteBusy(false);
    }
  };

  const handleRemove = async (member) => {
    const targetEmail = member.email || member.userSub;
    const isSelf = currentUserSub && member.userSub === currentUserSub;
    const promptText = isSelf
      ? `Leave this pool? You will lose access to "${poolLabel || poolId}".`
      : `Remove ${targetEmail} from "${poolLabel || poolId}"?`;
    if (!window.confirm(promptText)) return;
    try {
      await removePoolMember(networkId, poolId, member.userSub);
      refresh();
    } catch (ex) {
      setInviteMsg({ kind: 'error', text: ex.message || 'Remove failed' });
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/30"
          />
          <motion.aside
            key="drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            className="fixed right-0 top-0 z-50 flex h-screen max-h-screen w-full max-w-md flex-col overflow-hidden bg-white shadow-xl"
          >
            <header className="shrink-0 flex items-start justify-between gap-2 border-b border-gray-200 px-4 py-3">
              <div className="min-w-0">
                <h2 className="truncate text-sm font-semibold text-gray-900">
                  Pool settings · {poolLabel || poolId}
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">
                  Members, trigger mode, agent supervisors, and connector
                  overrides. Creators own every section; supervisors can view
                  and approve in the Room.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close pool settings drawer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18" strokeLinecap="round" />
                  <path d="M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
              <PoolSettingsCollapsible
                id="pool-settings-members"
                title="Members"
                subtitle="Invite workspace users · pool roster"
                open={settingsSectionOpen.members}
                onToggle={() => toggleSettingsSection('members')}
              >
                {canManage ? (
                  <form
                    onSubmit={handleInvite}
                    className="space-y-2 rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-3"
                  >
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="flex min-w-[180px] flex-1 flex-col text-xs font-medium text-gray-700">
                        Workspace email
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          placeholder="alice@acme.com"
                          className="mt-1 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
                        />
                      </label>
                      <button
                        type="submit"
                        disabled={inviteBusy}
                        className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {inviteBusy ? 'Adding…' : 'Add member'}
                      </button>
                    </div>
                    <fieldset className="rounded-md border border-gray-200 bg-white px-3 py-2">
                      <legend className="px-1 text-[11px] font-medium text-gray-700">Roles (multi-select)</legend>
                      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                        {ROOM_ROLE_OPTIONS.map((opt) => {
                          const checked = selectedRoles.includes(opt.value);
                          return (
                            <label
                              key={opt.value}
                              className={`flex items-start gap-2 rounded border px-2 py-1.5 text-xs cursor-pointer transition-colors ${
                                checked ? 'border-indigo-300 bg-indigo-50/60' : 'border-gray-200 bg-white hover:bg-gray-50'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleRole(opt.value)}
                                className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <div className="min-w-0">
                                <div className="font-semibold text-gray-800">{opt.label}</div>
                                <div className="text-[11px] text-gray-500">{opt.hint}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>
                    {inviteMsg ? (
                      <p
                        className={`text-xs ${
                          inviteMsg.kind === 'error'
                            ? 'text-red-600'
                            : 'text-emerald-700'
                        }`}
                      >
                        {inviteMsg.text}
                      </p>
                    ) : null}
                    <p className="text-[11px] text-gray-500">
                      Users must already be in the workspace. Out-of-workspace invites go through{' '}
                      <a
                        href="/workspace/members"
                        className="text-indigo-600 hover:underline"
                      >
                        Workspace → Members
                      </a>
                      .
                    </p>
                  </form>
                ) : null}
                <div
                  className={
                    canManage
                      ? 'mt-3 rounded-lg border border-gray-100 bg-gray-50/40 p-3'
                      : 'rounded-lg border border-gray-100 bg-gray-50/40 p-3'
                  }
                >
                  {loading ? (
                    <p className="text-xs text-gray-500">Loading…</p>
                  ) : error ? (
                    <p className="text-xs text-red-600">{error}</p>
                  ) : activeMembers.length === 0 ? (
                    <p className="text-xs text-gray-500">No active members yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {activeMembers.map((m) => {
                        const isSelf = currentUserSub && m.userSub === currentUserSub;
                        const isLastCreator =
                          (m.role || '').toUpperCase() === 'CREATOR' &&
                          activeCreatorCount <= 1;
                        const canRemove = canManage && !isLastCreator;
                        return (
                          <li
                            key={m.userSub}
                            className="rounded-lg border border-gray-200 bg-white px-3 py-2"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="truncate text-sm font-medium text-gray-900">
                                  {m.userName || m.email || m.userSub}
                                  {isSelf ? (
                                    <span className="ml-1 text-[11px] font-normal text-gray-500">
                                      (you)
                                    </span>
                                  ) : null}
                                </div>
                                {/* Secondary line: email when we led with the name (so both
                                    are visible); the sub is the hover title fallback. */}
                                {m.userName && m.email ? (
                                  <div className="truncate text-[11px] text-gray-500" title={m.userSub}>
                                    {m.email}
                                  </div>
                                ) : null}
                                <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
                                  <RoleBadge role={m.role} />
                                  {m.workspaceRole ? (
                                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
                                      Workspace: {m.workspaceRole}
                                    </span>
                                  ) : null}
                                  {m.joinedAt ? (
                                    <span>joined {m.joinedAt.slice(0, 10)}</span>
                                  ) : null}
                                </div>
                              </div>
                              {canRemove ? (
                                <button
                                  type="button"
                                  onClick={() => handleRemove(m)}
                                  className="shrink-0 rounded-md border border-red-200 px-2 py-1 text-[11px] font-medium text-red-700 hover:bg-red-50"
                                >
                                  {isSelf ? 'Leave' : 'Remove'}
                                </button>
                              ) : isLastCreator ? (
                                <span
                                  className="shrink-0 text-[11px] text-gray-400"
                                  title="Cannot remove the last CREATOR — promote another member first."
                                >
                                  last creator
                                </span>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </PoolSettingsCollapsible>

              <PoolSettingsCollapsible
                id="pool-settings-trigger"
                title="Trigger mode"
                subtitle="Runtime · who can start a run"
                open={settingsSectionOpen.trigger}
                onToggle={() => toggleSettingsSection('trigger')}
              >
                <p className="mb-2 text-[11px] text-gray-500">
                  Controls how runs can be initiated. The AI behavior of the network (prompts,
                  tools, ASL) is locked to the released version — only the start-up surface
                  changes here.
                </p>
                <fieldset
                  className="space-y-1"
                  disabled={!canManage || triggerBusy}
                >
                {listTriggerModes().map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex cursor-pointer items-start gap-2 rounded-md border px-2 py-1.5 text-xs transition ${
                      currentTrigger === opt.value
                        ? 'border-indigo-300 bg-indigo-50/60'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    } ${!canManage ? 'cursor-not-allowed opacity-70' : ''}`}
                  >
                    <input
                      type="radio"
                      name="pool-trigger-mode"
                      value={opt.value}
                      checked={currentTrigger === opt.value}
                      onChange={() => handleTriggerChange(opt.value)}
                      className="mt-0.5"
                    />
                    <span className="min-w-0">
                      <span className="block font-medium text-gray-900">
                        {opt.label}
                      </span>
                      <span className="block text-[11px] text-gray-500">
                        {opt.description}
                      </span>
                    </span>
                  </label>
                ))}
              </fieldset>
              {triggerMsg ? (
                <p
                  className={`mt-2 text-[11px] ${
                    triggerMsg.kind === 'error'
                      ? 'text-red-600'
                      : 'text-emerald-700'
                  }`}
                >
                  {triggerMsg.text}
                </p>
              ) : null}
              {!canManage ? (
                <p className="mt-2 text-[11px] text-gray-500">
                  Only pool creators or workspace admins can change this.
                </p>
              ) : null}
              </PoolSettingsCollapsible>

              <PoolSettingsCollapsible
                id="pool-settings-builder"
                title="Builder execution"
                subtitle="Runtime · autonomous vs interactive"
                open={settingsSectionOpen.builder}
                onToggle={() => toggleSettingsSection('builder')}
              >
                <p className="mb-2 text-[11px] text-gray-500">
                  Controls whether Claude Code auto-approves tool calls (autonomous) or
                  pauses for human approval from the room (interactive). Overrides the
                  network-level default for this room only.
                </p>
                <fieldset
                  className="space-y-1"
                  disabled={!canManage || builderModeBusy}
                >
                  {[
                    { value: 'inherit', label: 'Inherit from network', description: 'Use the network creator\'s setting' },
                    { value: 'background', label: 'Autonomous', description: 'Claude Code auto-approves all tool use' },
                    { value: 'interactive', label: 'Interactive', description: 'Pauses at each tool call for room approval' },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex cursor-pointer items-start gap-2 rounded-md border px-2 py-1.5 text-xs transition ${
                        (builderMode || 'inherit') === opt.value
                          ? 'border-indigo-300 bg-indigo-50/60'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      } ${!canManage ? 'cursor-not-allowed opacity-70' : ''}`}
                    >
                      <input
                        type="radio"
                        name="pool-builder-mode"
                        value={opt.value}
                        checked={(builderMode || 'inherit') === opt.value}
                        onChange={() => handleBuilderModeChange(opt.value)}
                        className="mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="block font-medium text-gray-900">{opt.label}</span>
                        <span className="block text-[11px] text-gray-500">{opt.description}</span>
                      </span>
                    </label>
                  ))}
                </fieldset>
                {builderModeMsg ? (
                  <p className={`mt-2 text-[11px] ${builderModeMsg.kind === 'error' ? 'text-red-600' : 'text-emerald-700'}`}>
                    {builderModeMsg.text}
                  </p>
                ) : null}
                {!canManage ? (
                  <p className="mt-2 text-[11px] text-gray-500">
                    Only pool creators or workspace admins can change this.
                  </p>
                ) : null}
              </PoolSettingsCollapsible>

              <PoolSettingsCollapsible
                id="pool-settings-supervisors"
                title="Agent supervisors"
                subtitle="Runtime · per-agent override"
                open={settingsSectionOpen.supervisors}
                onToggle={() => toggleSettingsSection('supervisors')}
              >
                <p className="mb-2 text-[11px] text-gray-500">
                  Pick an agent and edit its supervisor inbox the same way you would in
                  the Network Workbench Safety &amp; Permissions tab. Cleared overrides
                  fall back to the workbench-default supervisor configured at release
                  time.
                </p>

                {agentSupLoading ? (
                  <p className="mt-2 text-xs text-gray-500">Loading…</p>
                ) : agentSupError ? (
                  <p className="mt-2 text-xs text-red-600">{agentSupError}</p>
                ) : agentSupervisors.length === 0 ? (
                  <p className="mt-2 text-xs text-gray-500">
                    This pool has no agents that take supervisor input.
                  </p>
                ) : (() => {
                  const row = agentSupervisors.find(
                    (a) => a.agentId === selectedSupervisorAgentId,
                  );
                  const wbEmail = row?.workbenchDefaultEmail
                    || row?.workbenchSupervisor?.email
                    || '';
                  const baselineList = Array.isArray(row?.effectiveSupervisorEmails)
                      && row.effectiveSupervisorEmails.length
                    ? row.effectiveSupervisorEmails
                    : (row?.effectiveSupervisorEmail
                        ? [row.effectiveSupervisorEmail]
                        : (row?.effectiveSupervisor?.email
                            ? [row.effectiveSupervisor.email]
                            : []));
                  const isOverride =
                    String(row?.source || '').toUpperCase() === 'OVERRIDE'
                    || String(row?.source || '').toUpperCase() === 'POOL';
                  const isBusy = overrideBusyId === selectedSupervisorAgentId;
                  const rowMsg = overrideMsg && overrideMsg.agentId === selectedSupervisorAgentId
                    ? overrideMsg
                    : null;
                  // Detect dirty state by comparing the picker draft to
                  // the baseline (sorted, lowercased) — pure visual cue
                  // for the Save button.
                  const norm = (xs) => Array.from(new Set(
                    (Array.isArray(xs) ? xs : [])
                      .map((e) => String(e || '').trim().toLowerCase())
                      .filter(Boolean),
                  )).sort();
                  const draftDirty = JSON.stringify(norm(supervisorDraftEmails))
                    !== JSON.stringify(norm(baselineList));
                  return (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Agent
                        </label>
                        <select
                          value={selectedSupervisorAgentId}
                          onChange={(e) => setSelectedSupervisorAgentId(e.target.value)}
                          disabled={!canManage || isBusy}
                          className="block w-full max-w-xs rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                        >
                          {agentSupervisors.map((a) => (
                            <option key={a.agentId} value={a.agentId}>
                              {a.agentLabel || a.agentId}
                            </option>
                          ))}
                        </select>
                      </div>

                      {row ? (
                        <fieldset disabled={!canManage || isBusy}>
                          <legend className="sr-only">Supervisor list for selected agent</legend>
                          <div className="mb-1 flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
                            <span
                              className={`rounded-full px-2 py-0.5 font-medium ${
                                isOverride
                                  ? 'bg-amber-50 text-amber-800'
                                  : 'bg-gray-100 text-gray-700'
                              }`}
                              title={
                                isOverride
                                  ? 'Pool override — diverges from the released workbench supervisor'
                                  : 'Inherits the supervisor configured in the Workbench at release time'
                              }
                            >
                              {isOverride ? 'Pool override' : 'Workbench default'}
                            </span>
                            {isOverride && wbEmail ? (
                              <span className="text-gray-400">Default · {wbEmail}</span>
                            ) : null}
                          </div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Supervisors
                          </label>
                          <WorkspaceMembersMultiPicker
                            tenantId={tenantId}
                            value={supervisorDraftEmails}
                            onChange={setSupervisorDraftEmails}
                            disabled={isBusy}
                            emptyHint="Add one or more workspace members. Search by name or email — same directory as the Workbench Safety & Permissions tab."
                          />

                          {canManage ? (
                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  handleSetSupervisor(
                                    selectedSupervisorAgentId,
                                    supervisorDraftEmails,
                                  )
                                }
                                disabled={isBusy || !draftDirty}
                                className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                {isBusy ? 'Saving…' : 'Save'}
                              </button>
                              {isOverride ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleClearSupervisor(selectedSupervisorAgentId, wbEmail)
                                  }
                                  disabled={isBusy}
                                  className="rounded-md border border-amber-200 px-2.5 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50"
                                >
                                  {isBusy ? 'Resetting…' : 'Reset to default'}
                                </button>
                              ) : null}
                            </div>
                          ) : null}

                          {rowMsg ? (
                            <p
                              className={`mt-2 text-[11px] ${
                                rowMsg.kind === 'error' ? 'text-red-600' : 'text-emerald-700'
                              }`}
                            >
                              {rowMsg.text}
                            </p>
                          ) : null}
                        </fieldset>
                      ) : null}
                    </div>
                  );
                })()}

              {!canManage ? (
                <p className="mt-2 text-[11px] text-gray-500">
                  Read-only — only pool creators or workspace admins can
                  change agent supervisors at the pool level.
                </p>
              ) : null}
              </PoolSettingsCollapsible>

            {/* Phase D — per-agent connector overrides. UX mirrors the
                Network Workbench Agents → Tools & Routing → Tools field
                so room owners and builders use the same picker they
                already know from build time. See
                ``connector-context-hierarchy`` rule for the resolver. */}
              <PoolSettingsCollapsible
                id="pool-settings-connectors"
                title="Connectors"
                subtitle="Tools & secrets · per-agent · this room"
                open={settingsSectionOpen.connectors}
                onToggle={() => toggleSettingsSection('connectors')}
              >
                <p className="mb-2 text-[11px] text-gray-500">
                  Pick an agent and edit its tools the same way you would in the Network
                  Workbench. Pinning a connector here applies at runtime even if the
                  network admin didn't wire the tool on the agent at build time. Cleared
                  pins fall back along Tenant ⊃ Network ⊃ Pool.
                </p>

                {connectorLoading ? (
                  <p className="text-xs text-gray-500">Loading…</p>
                ) : connectorError ? (
                  <p className="text-xs text-red-600">{connectorError}</p>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Agent
                      </label>
                      <select
                        value={selectedConnectorAgentId}
                        onChange={(e) => setSelectedConnectorAgentId(e.target.value)}
                        disabled={!canManageConnectors || connectorAgentBusy}
                        className="block w-full max-w-xs rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                      >
                        <option value={ROOM_WIDE_AGENT_ID}>Room Wide (all agents)</option>
                        {agentConnectorAgents.map((a) => (
                          <option key={a.agentId} value={a.agentId}>
                            {a.agentLabel || a.agentId}
                          </option>
                        ))}
                      </select>
                      {selectedConnectorAgentId === ROOM_WIDE_AGENT_ID ? (
                        <p className="mt-1 text-[11px] text-gray-500">
                          Pins apply to every agent in this room. A per-agent pin still wins where set.
                        </p>
                      ) : null}
                    </div>

                    {selectedConnectorAgentId ? (
                      <fieldset disabled={!canManageConnectors || connectorAgentBusy}>
                        <legend className="sr-only">Tools for selected agent</legend>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Tools
                        </label>
                        <AgentToolPicker
                          value={connectorAgentDraft}
                          onChange={setConnectorAgentDraft}
                          tools={connectorToolsCatalog.tools}
                          loading={connectorToolsCatalog.loading}
                          error={connectorToolsCatalog.error}
                          networkId={networkId}
                          poolId={poolId}
                          agentId={selectedConnectorAgentId}
                          env={TOOLS_ENV}
                          tenantId={tenantId}
                          mcpCatalog={mcpCatalog}
                          mcpValue={mcpPinDraft}
                          onMcpChange={setMcpPinDraft}
                          mcpInstancesLoader={mcpInstancesLoader}
                          // Connector saved-connection list (type-tile → pick a
                          // saved connection). connectorChoices is already
                          // {toolName: [registry rows]} loaded for this scope.
                          connectorInstancesLoader={async () => {
                            const byTool = {};
                            for (const [tn, rows] of Object.entries(connectorChoices || {})) {
                              byTool[tn] = (rows || []).map((r) => ({
                                secretId: r.secretId,
                                secretName: r.secretName,
                                testStatus: r.testStatus,
                                scope: r.scope,
                              }));
                            }
                            return byTool;
                          }}
                          // Shared workspace pin (doc/arch/ASSETS.md) — single slot,
                          // ROOM-WIDE: one shared workspace for the whole room (every
                          // agent collaborates on the one tree; the runtime lease
                          // serialises writers). Only the Room-Wide scope offers it;
                          // per-agent picks don't carry a workspace, so the trio is
                          // null there.
                          workspaceCatalog={
                            selectedConnectorAgentId === ROOM_WIDE_AGENT_ID
                              ? workspaceChoices.map((a) => ({ id: a.secretId, label: a.secretName || a.secretId, scope: a.scope }))
                              : null
                          }
                          workspaceValue={workspacePinDraft}
                          onWorkspaceChange={
                            selectedConnectorAgentId === ROOM_WIDE_AGENT_ID ? setWorkspacePinDraft : null
                          }
                          onNavigateToConnections={onNavigateToConnections}
                        />

                        {canManageConnectors ? (
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={handleSaveAgentConnectors}
                              disabled={connectorAgentBusy}
                              className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                            >
                              {connectorAgentBusy ? 'Saving…' : 'Save'}
                            </button>
                            <button
                              type="button"
                              onClick={handleResetAgentConnectorsDraft}
                              disabled={connectorAgentBusy}
                              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Reset
                            </button>
                          </div>
                        ) : null}

                        {connectorMsg
                          && connectorMsg.scope === 'agent'
                          && connectorMsg.agentId === selectedConnectorAgentId ? (
                          <p
                            className={`mt-2 text-[11px] ${
                              connectorMsg.kind === 'error' ? 'text-red-600' : 'text-emerald-700'
                            }`}
                          >
                            {connectorMsg.text}
                          </p>
                        ) : null}
                      </fieldset>
                    ) : null}
                  </div>
                )}

                {!canManageConnectors ? (
                  <p className="mt-3 text-[11px] text-gray-500">
                    Read-only — only pool OWNER, BUILDER, or workspace OWNER/ADMIN can pin
                    connectors.
                  </p>
                ) : null}
              </PoolSettingsCollapsible>

              <PoolSettingsCollapsible
                id="pool-settings-room-context"
                title="Room context"
                subtitle="Runtime · inject overall context into runs"
                open={settingsSectionOpen.roomContext}
                onToggle={() => toggleSettingsSection('roomContext')}
              >
                <p className="mb-3 text-[11px] text-gray-500">
                  When on, this room's overall context (compiled from its memory +
                  execution outcomes) is injected — read-only — into each run so agents
                  build on it. <span className="font-medium text-gray-700">Inherit</span>{' '}
                  follows the network's setting; <span className="font-medium text-gray-700">On</span>/
                  <span className="font-medium text-gray-700">Off</span> override it for this room only.
                </p>
                {roomCtxLoading ? (
                  <p className="text-xs text-gray-500">Loading…</p>
                ) : (
                  <div
                    className="inline-flex overflow-hidden rounded-lg border border-gray-200"
                    role="radiogroup"
                    aria-label="Room context injection override"
                    data-testid="room-ctx-override-control"
                  >
                    {[
                      { key: 'inherit', label: 'Inherit', val: null },
                      { key: 'on', label: 'On', val: 'on' },
                      { key: 'off', label: 'Off', val: 'off' },
                    ].map((opt, i) => {
                      const selected = roomCtxOverride === opt.val;
                      return (
                        <button
                          key={opt.key}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          disabled={!canManageConnectors || roomCtxBusy}
                          onClick={() => applyRoomCtxOverride(opt.key)}
                          data-testid={`room-ctx-override-${opt.key}`}
                          className={`px-3 py-1.5 text-xs font-medium transition-colors ${i > 0 ? 'border-l border-gray-200' : ''} ${
                            selected
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-50'
                          } ${(!canManageConnectors || roomCtxBusy) ? 'cursor-not-allowed opacity-60' : ''}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                {!canManageConnectors ? (
                  <p className="mt-2 text-[11px] text-gray-400">
                    Only a room owner / creator can change this.
                  </p>
                ) : null}

                {/* Objective-judge tier (SCHEDULER.md §11.1b) — which reasoning
                    tier grades a scheduled loop's result. Model name abstracted. */}
                <div className="mt-4 border-t border-gray-100 pt-3">
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Judge intelligence
                  </label>
                  <p className="mb-2 text-[11px] text-gray-500">
                    How much reasoning power grades a scheduled loop's result
                    against its Objective. Higher tiers judge more rigorously.
                  </p>
                  <select
                    value={judgeModel}
                    disabled={!canManageConnectors || judgeModelBusy || roomCtxLoading}
                    onChange={(e) => applyJudgeModel(e.target.value)}
                    data-testid="judge-model-select"
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm disabled:opacity-60"
                  >
                    <option value="">Default (recommended)</option>
                    <option value="fast">Fast — quick, lightweight</option>
                    <option value="think">Think — balanced</option>
                    <option value="reason">Reason — most rigorous</option>
                  </select>
                </div>

                {roomCtxError ? (
                  <p className="mt-2 text-[11px] text-red-600">{roomCtxError}</p>
                ) : null}
              </PoolSettingsCollapsible>

            </div>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  );
}
